import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SEO from '../../components/SEO';
import Navbar from '../../component/Navbar/Navbar';
import { api, postAuthLogin } from '../../api/client';
import { PasswordFieldToggle } from '../../components/PasswordFieldToggle';
import { useAuth } from '../../context/AuthContext';
import { getPostLoginPathFromUser } from '../../utils/postLoginRedirect';
import Footer from '../../component/Footer/Footer';
import './SetPassword.css';

const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [errorList, setErrorList] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendStatus, setResendStatus] = useState(null);
  const [resending, setResending] = useState(false);
  const [postLoginPath, setPostLoginPath] = useState('/my-bookings');

  const toErrorList = (err) => {
    const bag = err?.data?.errors;
    if (bag && typeof bag === 'object') {
      const messages = Object.values(bag).flat().filter(Boolean);
      return messages.length > 0 ? messages : [err?.message || 'Failed to set password.'];
    }
    return [err?.message || err?.data?.message || 'Failed to set password. The link may have expired.'];
  };

  useEffect(() => {
    const token = searchParams.get('token');
    const emailParam = searchParams.get('email');
    if (emailParam) setEmail(emailParam);
    if (!token || !emailParam) setErrorList(['Invalid or missing link. Please use the password reset link from your email.']);
  }, [searchParams]);

  const handleResendVerification = async () => {
    if (!email) return;
    setResendStatus(null);
    setResending(true);
    try {
      const data = await api.post('/auth/resend-verification-email', { email }, null);
      setResendStatus({ ok: true, message: data?.message || 'Check your inbox for the verification link.' });
    } catch (err) {
      setResendStatus({
        ok: false,
        message: err?.message || err?.data?.message || 'Could not resend. Try again later.',
      });
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorList([]);
    setFieldErrors({});
    setNeedsVerification(false);
    if (password !== passwordConfirmation) {
      const msg = 'Passwords do not match.';
      setFieldErrors({ password_confirmation: msg });
      setErrorList([msg]);
      return;
    }
    if (password.length < 8) {
      const msg = 'Password must be at least 8 characters.';
      setFieldErrors({ password: msg });
      setErrorList([msg]);
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', {
        token: searchParams.get('token'),
        email,
        password,
        password_confirmation: passwordConfirmation,
      }, null);
      try {
        const loginRes = await postAuthLogin(email, password);
        const token = loginRes?.access_token ?? loginRes?.data?.access_token;
        let nextPath = '/my-bookings';
        if (token) {
          localStorage.setItem('luxistt_token', token);
          const me = await refreshUser();
          nextPath = getPostLoginPathFromUser(me);
          setPostLoginPath(nextPath);
        }
        setSuccess(true);
        window.setTimeout(() => navigate(nextPath, { replace: true }), 700);
      } catch (loginErr) {
        if (loginErr.status === 403 && String(loginErr.message || '').toLowerCase().includes('verify')) {
          setNeedsVerification(true);
          setErrorList([
            loginErr.message || 'Please verify your email before signing in.',
            'If you did not receive the verification email, use the button below to resend it.',
          ]);
        } else {
          setErrorList(toErrorList(loginErr));
        }
      }
    } catch (err) {
      setErrorList(toErrorList(err));
      if (err?.data?.errors && typeof err.data.errors === 'object') {
        const perField = {};
        Object.entries(err.data.errors).forEach(([key, messages]) => {
          if (Array.isArray(messages) && messages.length > 0) {
            perField[key] = messages[0];
          }
        });
        setFieldErrors(perField);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="set-password-page">
        <SEO title="Password set" description="Your Luxistt account password has been saved." path="/set-password" noindex />
        <Navbar />
        <div className="set-password-main">
          <div className="set-password-card">
            <h1>Password set</h1>
            <p>Your account is ready. Redirecting you…</p>
            <Link to={postLoginPath} className="set-password-link">
              {postLoginPath === '/front-desk'
                ? 'Go to Front Desk'
                : postLoginPath === '/dashboard'
                  ? 'Go to Dashboard'
                  : 'Go to My Bookings'}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="set-password-page">
      <SEO title="Set your password" description="Choose a password for your Luxistt account." path="/set-password" noindex />
      <Navbar />
      <div className="set-password-main">
        <div className="set-password-card">
          <h1>Set your password</h1>
          <p>Choose a password to sign in to your account.</p>
          {errorList.length > 0 && (
            <div className="login-error" role="alert">
              <ul className="login-error-list">
                {errorList.map((msg, idx) => <li key={`${msg}-${idx}`}>{msg}</li>)}
              </ul>
            </div>
          )}
          {needsVerification && (
            <div className="set-password-resend">
              <button
                type="button"
                className="set-password-resend-btn"
                onClick={handleResendVerification}
                disabled={resending || !email}
              >
                {resending ? 'Sending…' : 'Resend verification email'}
              </button>
              {resendStatus && (
                <p className={resendStatus.ok ? 'set-password-resend-ok' : 'set-password-resend-err'}>
                  {resendStatus.message}
                </p>
              )}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <label htmlFor="set-password-email">Email</label>
            <input
              id="set-password-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              readOnly={!!searchParams.get('email')}
              className={fieldErrors.email ? 'set-password-input-error' : ''}
            />
            {fieldErrors.email && <small className="set-password-field-error">{fieldErrors.email}</small>}
            <PasswordFieldToggle
              id="set-password-pass"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              placeholder="At least 8 characters"
              inputClassName={fieldErrors.password ? 'set-password-input-error' : ''}
              errorText={fieldErrors.password}
              errorClassName="set-password-field-error"
            />
            <PasswordFieldToggle
              id="set-password-pass2"
              label="Confirm password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              required
              minLength={8}
              placeholder="Repeat password"
              inputClassName={fieldErrors.password_confirmation ? 'set-password-input-error' : ''}
              errorText={fieldErrors.password_confirmation}
              errorClassName="set-password-field-error"
            />
            <button type="submit" disabled={submitting || !searchParams.get('token')}>
              {submitting ? 'Setting password…' : 'Set password'}
            </button>
          </form>
          <p className="set-password-footer">
            <Link to="/login">Back to login</Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SetPassword;
